// INVXFER1-S2 (docs/build-briefs/INVXFER1-stock-transfers-between-bars.md section 9; design/mockups/invxfer1-s2/): the
// stock transfer screens as DATA. Pure and DOM free, so a plain node:test file can import it
// (test/ui-transfers-model.test.ts) and ui/src/transfers.ts draws from it.
//
// Quantities are exact decimal strings, never floats: every step and every scaling is BigInt arithmetic at the
// ledger's four decimals. PROVISIONAL (brief section 9): steps are a tenth of the item's count unit, with a whole
// "+1" step beside the stepper.
//
// Nothing here decides access beyond `transferGates`, which reads permission ids through the caller's own check.

import { NOT_AVAILABLE } from "./functionHubsModel.ts";
import { compareNames } from "./nameSort.ts";

export { NOT_AVAILABLE };

export type TransferState = "sent" | "received" | "cancelled";

export const TRANSFER_PERMISSIONS = {
  view: "inv.transfer.view",
  send: "inv.transfer.send",
  receive: "inv.transfer.receive",
} as const;

export interface TransferGates {
  readonly view: boolean;
  readonly send: boolean;
  readonly receive: boolean;
}

/** Every control reads its own permission id; a person holding none of the three sees nothing. */
export function transferGates(has: (permissionId: string) => boolean): TransferGates {
  return {
    view: has(TRANSFER_PERMISSIONS.view),
    send: has(TRANSFER_PERMISSIONS.send),
    receive: has(TRANSFER_PERMISSIONS.receive),
  };
}

// ---------------------------------------------------------------------
// Exact quantities
// ---------------------------------------------------------------------

const SCALE = 10_000n;
const TENTH = 1_000n;

/** A decimal string as ten-thousandths. Null for anything that is not a plain non-negative decimal. */
export function toScaled(value: string): bigint | null {
  const match = /^(\d+)(?:\.(\d{1,4})\d*)?$/.exec(value.trim());
  if (match === null) return null;
  const whole = BigInt(match[1] ?? "0");
  const fraction = BigInt((match[2] ?? "").padEnd(4, "0") || "0");
  return whole * SCALE + fraction;
}

/** Ten-thousandths as the shortest exact decimal string ("2", "3.2", "0.1"). */
export function fromScaled(scaled: bigint): string {
  const negative = scaled < 0n;
  const abs = negative ? -scaled : scaled;
  const whole = abs / SCALE;
  const fraction = (abs % SCALE).toString().padStart(4, "0").replace(/0+$/, "");
  const text = fraction.length === 0 ? whole.toString() : `${whole}.${fraction}`;
  return negative ? `-${text}` : text;
}

/** A server quantity as it reads on screen: trailing zeros dropped. */
export function formatQuantity(value: string): string {
  const scaled = toScaled(value);
  return scaled === null ? value : fromScaled(scaled);
}

/** "bottle" or "bottles", by the quantity. "oz" never takes an s. */
export function unitLabel(unit: string, quantity: string): string {
  if (unit === "oz") return "oz";
  return quantity === "1" ? unit : `${unit}s`;
}

/** The unit beside a stepper: always the plural, since the figure moves between 0 and many. */
export function pluralUnit(unit: string): string {
  return unit === "oz" ? "oz" : `${unit}s`;
}

/** "2 bottles", "1 bottle", "3.2 bottles". */
export function quantityWithUnit(quantity: string, unit: string): string {
  const text = formatQuantity(quantity);
  return `${text} ${unitLabel(unit, text)}`;
}

export type StepKind = "down" | "up" | "whole";

/** One tap on a stepper: a tenth down, a tenth up, or a whole one up, held inside [min, max]. */
export function step(current: string, kind: StepKind, min: string, max: string): string {
  const now = toScaled(current) ?? 0n;
  const lo = toScaled(min) ?? 0n;
  const hi = toScaled(max) ?? 0n;
  let next = kind === "down" ? now - TENTH : kind === "up" ? now + TENTH : now + SCALE;
  if (next > hi) next = hi;
  if (next < lo) next = lo;
  return fromScaled(next);
}

/** Whether a step in this direction can still change the value (the button is dimmed when it cannot). */
export function canStep(current: string, kind: StepKind, min: string, max: string): boolean {
  return step(current, kind, min, max) !== formatQuantity(current);
}

// ---------------------------------------------------------------------
// New transfer
// ---------------------------------------------------------------------

export interface LocationDto {
  readonly id: string;
  readonly name: string;
  readonly isThisDevice: boolean;
}

/** The destination chips: the same owner's other locations, never this device's own. */
export function destinationLocations(locations: readonly LocationDto[]): LocationDto[] {
  return locations.filter((siteLocation) => !siteLocation.isThisDevice);
}

export function thisLocation(locations: readonly LocationDto[]): LocationDto | undefined {
  return locations.find((siteLocation) => siteLocation.isThisDevice);
}

export function locationName(locations: readonly LocationDto[], id: string): string {
  return locations.find((siteLocation) => siteLocation.id === id)?.name ?? NOT_AVAILABLE;
}

export interface StockProductInput {
  readonly id: string;
  readonly name: string;
  readonly active: boolean;
  readonly onHandInCountUnit?: string | null | undefined;
  readonly countUnit?: string | null | undefined;
}

export interface SendableItem {
  readonly id: string;
  readonly name: string;
  readonly onHand: string;
  readonly unit: string;
}

/** Items with a positive on hand in a count unit at this location. An item the server could not put in a count unit is
 * left out, never guessed. */
export function sendableItems(products: readonly StockProductInput[]): SendableItem[] {
  const items: SendableItem[] = [];
  for (const product of products) {
    if (!product.active) continue;
    const unit = product.countUnit;
    const onHand = product.onHandInCountUnit;
    if (unit === null || unit === undefined || onHand === null || onHand === undefined) continue;
    const scaled = toScaled(onHand);
    if (scaled === null || scaled <= 0n) continue;
    items.push({ id: product.id, name: product.name, onHand: fromScaled(scaled), unit });
  }
  return items.sort((a, b) => compareNames(a.name, b.name));
}

export interface SendLine {
  readonly item: SendableItem;
  readonly quantity: string;
}

/** The quantity a line starts at: one whole unit, or all of it when there is less than one. */
export function startingSendQuantity(item: SendableItem): string {
  const onHand = toScaled(item.onHand) ?? 0n;
  return onHand < SCALE ? fromScaled(onHand) : "1";
}

export function addSendLine(lines: readonly SendLine[], item: SendableItem): SendLine[] {
  if (lines.some((line) => line.item.id === item.id)) return [...lines];
  return [...lines, { item, quantity: startingSendQuantity(item) }];
}

/** Send never goes below a tenth or above on hand. */
export function stepSendLine(line: SendLine, kind: StepKind): SendLine {
  const onHand = toScaled(line.item.onHand) ?? 0n;
  const min = onHand < TENTH ? line.item.onHand : "0.1";
  return { ...line, quantity: step(line.quantity, kind, min, line.item.onHand) };
}

export function removeSendLine(lines: readonly SendLine[], itemId: string): SendLine[] {
  return lines.filter((line) => line.item.id !== itemId);
}

export function sendTitle(count: number): string {
  return `Sending ${count} ${count === 1 ? "item" : "items"}`;
}

export function sendButtonLabel(destination: LocationDto | undefined): string {
  return destination === undefined ? "Send" : `Send to ${destination.name}`;
}

/** Send is offered only with a destination and at least one line. */
export function canSend(lines: readonly SendLine[], destination: LocationDto | undefined): boolean {
  return destination !== undefined && lines.length > 0;
}

export function sendBody(
  lines: readonly SendLine[],
  destination: LocationDto,
  idempotencyKey: string,
): {
  idempotencyKey: string;
  toLocationId: string;
  lines: Array<{ liquorProductId: string; quantity: string; unit: string }>;
} {
  return {
    idempotencyKey,
    toLocationId: destination.id,
    lines: lines.map((line) => ({
      liquorProductId: line.item.id,
      quantity: line.quantity,
      unit: line.item.unit,
    })),
  };
}

// ---------------------------------------------------------------------
// The server's transfer shapes (src/domain/inventoryTransfers.ts)
// ---------------------------------------------------------------------

export interface TransferRowInput {
  readonly id: string;
  readonly from_location_id: string;
  readonly to_location_id: string;
  readonly state: TransferState;
  readonly sent_at: string;
  readonly line_count?: number | undefined;
  readonly sent_by_display_name?: string | null | undefined;
}

export interface TransferLineInput {
  readonly id: string;
  readonly componentReference: string;
  readonly quantity: string;
  readonly unit: string;
  readonly enteredQuantity: string | null;
  readonly enteredUnit: string | null;
  readonly receivedQuantity: string | null;
  readonly exception: { readonly shortQuantity: string; readonly note: string | null } | null;
}

// ---------------------------------------------------------------------
// List
// ---------------------------------------------------------------------

export type TransferFilter = "all" | "sent" | "received" | "cancelled";

export const TRANSFER_FILTERS: ReadonlyArray<{ id: TransferFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "sent", label: "Sent" },
  { id: "received", label: "Received" },
  { id: "cancelled", label: "Cancelled" },
];

export function filterTransfers<T extends { state: TransferState }>(
  rows: readonly T[],
  filter: TransferFilter,
): T[] {
  return filter === "all" ? [...rows] : rows.filter((row) => row.state === filter);
}

/** The count shown on the Sent chip (the mockup's "Sent 2"); no count on the other chips. */
export function filterCount(
  rows: readonly { state: TransferState }[],
  filter: TransferFilter,
): number | undefined {
  return filter === "sent" ? rows.filter((row) => row.state === "sent").length : undefined;
}

export const STATE_LABELS: Readonly<Record<TransferState, string>> = {
  sent: "Sent",
  received: "Received",
  cancelled: "Cancelled",
};

export function itemsText(count: number | undefined): string {
  if (count === undefined) return NOT_AVAILABLE;
  return `${count} ${count === 1 ? "item" : "items"}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Oct 13, 2:40 PM", read in the device's own zone. N/A for a value that is not a date. */
export function dateTimeText(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return NOT_AVAILABLE;
  const hours = date.getHours();
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${hour12}:${minutes} ${hours < 12 ? "AM" : "PM"}`;
}

/** "Oct 13" for the receive screen's eyebrow. */
export function shortDateText(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return NOT_AVAILABLE;
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

export interface TransferRowView {
  readonly id: string;
  readonly state: TransferState;
  readonly stateLabel: string;
  readonly from: string;
  readonly to: string;
  readonly when: string;
  readonly items: string;
}

export function transferRowView(
  row: TransferRowInput,
  locations: readonly LocationDto[],
): TransferRowView {
  return {
    id: row.id,
    state: row.state,
    stateLabel: STATE_LABELS[row.state],
    from: locationName(locations, row.from_location_id),
    to: locationName(locations, row.to_location_id),
    when: dateTimeText(row.sent_at),
    items: itemsText(row.line_count),
  };
}

/** The Inventory row's badge: transfers Sent to this location and not yet received. */
export function transfersToReceive(
  rows: readonly { state: TransferState; to_location_id: string }[],
  thisLocationId: string,
): number {
  return rows.filter((row) => row.state === "sent" && row.to_location_id === thisLocationId).length;
}

// ---------------------------------------------------------------------
// Receive
// ---------------------------------------------------------------------

/** The unit a line is shown and stepped in: what the sender entered, else the ledger unit. */
export function lineDisplay(line: TransferLineInput): { quantity: string; unit: string } {
  if (line.enteredQuantity !== null && line.enteredUnit !== null) {
    return { quantity: formatQuantity(line.enteredQuantity), unit: line.enteredUnit };
  }
  return { quantity: formatQuantity(line.quantity), unit: line.unit };
}

export interface ReceiveLineState {
  readonly line: TransferLineInput;
  /** In the display unit. */
  readonly received: string;
}

export function startReceive(lines: readonly TransferLineInput[]): ReceiveLineState[] {
  return lines.map((line) => ({ line, received: lineDisplay(line).quantity }));
}

/** Receive never goes below zero or above what was sent. */
export function stepReceiveLine(state: ReceiveLineState, kind: StepKind): ReceiveLineState {
  return {
    ...state,
    received: step(state.received, kind, "0", lineDisplay(state.line).quantity),
  };
}

function roundedDiv(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

/** What goes to the server for one line. The receive route reads the figure in the unit the line was SENT in (the
 * sender's count unit when a conversion happened, else the ledger unit) and converts it itself, so this is the
 * stepper's own figure, never above what was sent. Nothing is scaled here. */
export function receivedForServer(state: ReceiveLineState): string {
  const sent = toScaled(lineDisplay(state.line).quantity) ?? 0n;
  const received = toScaled(state.received) ?? 0n;
  return fromScaled(received > sent ? sent : received);
}

export interface ShortRow {
  readonly name: string;
  readonly short: string;
  readonly unit: string;
}

export function shortRows(states: readonly ReceiveLineState[]): ShortRow[] {
  const rows: ShortRow[] = [];
  for (const state of states) {
    const display = lineDisplay(state.line);
    const sent = toScaled(display.quantity) ?? 0n;
    const received = toScaled(state.received) ?? 0n;
    if (received < sent) {
      rows.push({
        name: state.line.componentReference,
        short: fromScaled(sent - received),
        unit: display.unit,
      });
    }
  }
  return rows;
}

export function shortTitle(count: number): string {
  return `Short ${count} ${count === 1 ? "item" : "items"}`;
}

export function shortRowText(row: ShortRow): string {
  return `${row.name}: ${formatQuantity(row.short)} short`;
}

/** PROVISIONAL (brief section 9): one pick-list per transfer, optional. */
export const EXCEPTION_REASONS: readonly string[] = [
  "Damaged",
  "Not in box",
  "Wrong item",
  "Other",
];

export function receiveBody(
  states: readonly ReceiveLineState[],
  reason: string | undefined,
  idempotencyKey: string,
): {
  idempotencyKey: string;
  lines: Array<{ lineId: string; receivedQuantity: string }>;
  exceptionNote?: string;
} {
  const lines = states.map((state) => ({
    lineId: state.line.id,
    receivedQuantity: receivedForServer(state),
  }));
  const anyShort = shortRows(states).length > 0;
  return reason !== undefined && anyShort && EXCEPTION_REASONS.includes(reason)
    ? { idempotencyKey, lines, exceptionNote: reason }
    : { idempotencyKey, lines };
}

// ---------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------

export interface DetailLineView {
  readonly name: string;
  /** What was sent, "2 bottles". */
  readonly sent: string;
  /** What arrived, set only once received. */
  readonly received: string | undefined;
  /** "Damaged" and the like, set only on a short line that carries a note. */
  readonly note: string | undefined;
}

/** The received ledger quantity back in the display unit, half up at four decimals. */
export function receivedDisplay(line: TransferLineInput): string | undefined {
  if (line.receivedQuantity === null) return undefined;
  const display = lineDisplay(line);
  if (line.enteredQuantity === null) return formatQuantity(line.receivedQuantity);
  const ledgerSent = toScaled(line.quantity) ?? 0n;
  const receivedLedger = toScaled(line.receivedQuantity) ?? 0n;
  if (ledgerSent === 0n) return display.quantity;
  if (receivedLedger >= ledgerSent) return display.quantity;
  const sentDisplay = toScaled(display.quantity) ?? 0n;
  return fromScaled(roundedDiv(sentDisplay * receivedLedger, ledgerSent));
}

export function detailLines(lines: readonly TransferLineInput[]): DetailLineView[] {
  return lines.map((line) => {
    const display = lineDisplay(line);
    const received = receivedDisplay(line);
    return {
      name: line.componentReference,
      sent: quantityWithUnit(display.quantity, display.unit),
      received: received === undefined ? undefined : quantityWithUnit(received, display.unit),
      note: line.exception?.note ?? undefined,
    };
  });
}

export function detailItemsTitle(count: number): string {
  return `${count} ${count === 1 ? "ITEM" : "ITEMS"}`;
}

/** The status line of the detail card. */
export function detailStatus(state: TransferState, toName: string, shortCount: number): string {
  if (state === "sent") return `Waiting for ${toName}`;
  if (state === "cancelled") return "Cancelled";
  return shortCount > 0 ? `Received, ${shortCount} short` : "Received";
}

export interface DetailActions {
  readonly canCancel: boolean;
  readonly canReceive: boolean;
}

/** Cancel is the sending location's, Receive the destination's, both only while Sent and both gated by permission. A
 * device at neither location is offered neither. */
export function detailActions(
  row: { state: TransferState; from_location_id: string; to_location_id: string },
  thisLocationId: string | undefined,
  gates: TransferGates,
): DetailActions {
  const sent = row.state === "sent" && thisLocationId !== undefined;
  return {
    canCancel: sent && gates.send && row.from_location_id === thisLocationId,
    canReceive: sent && gates.receive && row.to_location_id === thisLocationId,
  };
}

/** The list's "+ New transfer": needs send permission and a location to send to. */
export function canStartTransfer(gates: TransferGates, locations: readonly LocationDto[]): boolean {
  return gates.send && destinationLocations(locations).length > 0;
}
